// Centralized API configuration and client

import { RefreshTokenManager } from './refreshTokenManager';
import { logger } from '../logging';

const AUTH_EXEMPT_ENDPOINTS = [
  'users/refresh-token',
  'users/login',
  'users/register',
  'users/forgot-password',
  'users/reset-password',
];

const normalizeEndpointPath = (endpoint: string): string => {
  const withoutQuery = endpoint.split('?')[0].toLowerCase().replace(/\/+$/, '');
  return withoutQuery.startsWith('api/')
    ? withoutQuery.slice(4)
    : withoutQuery.replace(/^\/+/, '');
};

const shouldSkipTokenRefresh = (endpoint: string): boolean => {
  const path = normalizeEndpointPath(endpoint);
  return AUTH_EXEMPT_ENDPOINTS.includes(path);
};

const isBusinessRuleUnauthorized = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return normalized.includes('free tier')
    || normalized.includes('invalid username or password');
};

// Utility function to handle authentication errors and logout
const handleAuthError = (errorMsg: string, status: number) => {
  if (status !== 401) {
    return;
  }

  if (isBusinessRuleUnauthorized(errorMsg)) {
    return;
  }

  const message = errorMsg.toLowerCase();
  const isAuthFailure =
    message.includes('invalid user')
    || message.includes('invalid access token')
    || message.includes('access token expired')
    || message.includes('user not authenticated')
    || message.includes('invalid or expired refresh token')
    || RefreshTokenManager.isTokenExpired(0)
    || !localStorage.getItem('token');

  if (isAuthFailure) {
    RefreshTokenManager.clearTokens();
    window.location.href = '/logout';
  }
};

export class ApiClient {
  // Configuration - reads from environment variables or uses defaults
  private static get baseUrl(): string {
    // Try environment variable first (for production builds)
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }
    
    // Fallback to environment-based configuration
    if (process.env.NODE_ENV === 'production') {
      // In production, throw an error if no API URL is configured
      throw new Error('REACT_APP_API_URL environment variable must be set in production');
    }
    
    // Development API URL
    return 'http://localhost:5000';
  }

  // Helper method to build full API URL
  private static buildUrl(endpoint: string): string {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    
    // If baseUrl is empty, return relative URL (shouldn't happen with new config)
    if (this.baseUrl === '') {
      return `/${cleanEndpoint}`;
    }
    
    return `${this.baseUrl}/${cleanEndpoint}`;
  }

  private static buildHeaders(extraHeaders?: HeadersInit): HeadersInit {
    const token = localStorage.getItem('token');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (extraHeaders) {
      const extra = new Headers(extraHeaders);
      extra.forEach((value, key) => {
        headers[key] = value;
      });
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  // Generic fetch wrapper with common headers
  private static async fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<Response> {
    if (!shouldSkipTokenRefresh(endpoint)) {
      await RefreshTokenManager.ensureValidToken();
    }

    const config: RequestInit = {
      ...options,
      headers: this.buildHeaders(options.headers),
    };

    const response = await fetch(this.buildUrl(endpoint), config);

    if (
      response.status === 401
      && !isRetry
      && !shouldSkipTokenRefresh(endpoint)
      && RefreshTokenManager.hasRefreshToken()
    ) {
      const refreshed = await RefreshTokenManager.refreshToken();
      if (refreshed) {
        return this.fetchWithAuth(endpoint, options, true);
      }
    }

    return response;
  }

  // Centralized error handling for API responses
  private static async handleResponse(response: Response): Promise<any> {
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
        let error = new Error();
        try {
          const errorData = await response.json();
          const errorMessage = errorData.msg || 'Request failed';
          error = new Error(errorMessage);
          (error as any).status = response.status;
          
          // Handle authentication errors
          handleAuthError(errorMessage, response.status);
          
        } catch (parseError) {
          logger.error('Failed to parse API error response', parseError, {
            sourceFile: 'apiClient.ts',
            sourceClass: 'ApiClient',
            sourceLayer: 'Utils',
            status: response.status,
            url: response.url,
          });
          error = new Error('Failed to parse error response');
          (error as any).status = response.status;
          
          // Handle auth errors even when response can't be parsed
          handleAuthError('Failed to parse error response', response.status);
        }        
        throw error;
    }
  }

  // GET request with error handling
  static async get(endpoint: string, options?: RequestInit): Promise<any> {
    try {
      const response = await this.fetchWithAuth(endpoint, { ...options, method: 'GET' });
      return await this.handleResponse(response);
    } catch (error) {
      if (!(error as any).status || (error as any).status !== 401) {
        logger.error('API GET request failed', error, {
          sourceFile: 'apiClient.ts',
          sourceClass: 'ApiClient',
          sourceLayer: 'Utils',
          method: 'GET',
          endpoint,
        });
      }
      throw error;
    }
  }

  // POST request with error handling
  static async post(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    try {
      const response = await this.fetchWithAuth(endpoint, {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      });
      return await this.handleResponse(response);
    } catch (error) {
      if (!(error as any).status || (error as any).status !== 401) {
        logger.error('API POST request failed', error, {
          sourceFile: 'apiClient.ts',
          sourceClass: 'ApiClient',
          sourceLayer: 'Utils',
          method: 'POST',
          endpoint,
        });
      }
      throw error;
    }
  }

  // PUT request with error handling
  static async put(endpoint: string, data?: any, options?: RequestInit): Promise<any> {
    try {
      const response = await this.fetchWithAuth(endpoint, {
        ...options,
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      });
      return await this.handleResponse(response);
    } catch (error) {
      if (!(error as any).status || (error as any).status !== 401) {
        logger.error('API PUT request failed', error, {
          sourceFile: 'apiClient.ts',
          sourceClass: 'ApiClient',
          sourceLayer: 'Utils',
          method: 'PUT',
          endpoint,
        });
      }
      throw error;
    }
  }

  // DELETE request with error handling
  static async delete(endpoint: string, options?: RequestInit): Promise<any> {
    try {
      const response = await this.fetchWithAuth(endpoint, { ...options, method: 'DELETE' });
      return await this.handleResponse(response);
    } catch (error) {
      if (!(error as any).status || (error as any).status !== 401) {
        logger.error('API DELETE request failed', error, {
          sourceFile: 'apiClient.ts',
          sourceClass: 'ApiClient',
          sourceLayer: 'Utils',
          method: 'DELETE',
          endpoint,
        });
      }
      throw error;
    }
  }

  // Utility method to get the current API base URL (useful for debugging)
  static getCurrentBaseUrl(): string {
    return this.baseUrl;
  }

  // Utility method to check if we're in production mode
  static isProduction(): boolean {
    return process.env.NODE_ENV === 'production';
  }
}

export default ApiClient;
